import { canv } from "@/canv/global";
import { lightOrDark } from "@/canv/utils";
import { getViewport } from "@/canvasController/helpers";

interface Viewport {
    viewportStartX: number;
    viewportStartY: number;
    viewportWidth: number;
    viewportHeight: number;
}

export class VirtualCursor {
    name: string;
    color: string;
    fontColor: string;

    x = 0;
    y = 0;
    deltaX = 0;
    deltaY = 0;
    isMoving = false;
    moveCounter = 0;
    hasNext = false;
    nextDeltaX = 0;
    nextDeltaY = 0;
    moveCounterConst = 30;

    fontSize = 15;
    nameHeight = 0;
    nameWidth = 0;
    padding = 1;
    cursorPadding = 15;
    isHidden = false;

    constructor(name: string, color: string) {
        this.name = name;
        this.color = color;
        this.fontColor = "white";
        // this.fontColor = lightOrDark(this.color) === "light" ? "black" : "white";
        this.calcDims();
    }

    private drawRoundedRect(x: number, y: number, w: number, h: number, r: number): void {
        let newR = r;
        if (w < 2 * r) newR = w / 2;
        if (h < 2 * r) newR = h / 2;
        canv.ctx.beginPath();
        canv.ctx.moveTo(x + r, y);
        canv.ctx.arcTo(x + w, y, x + w, y + h, r);
        canv.ctx.arcTo(x + w, y + h, x, y + h, r);
        canv.ctx.arcTo(x, y + h, x, y, r);
        canv.ctx.arcTo(x, y, x + w, y, r);
        canv.ctx.closePath();
    }

    private calcDims(): void {
        this.padding = canv.actionManager.getZoomConstant(8);
        this.fontSize = canv.actionManager.getZoomConstant(16);
        canv.ctx.font = `${this.fontSize}px sans-serif`;
        const metrics: TextMetrics = canv.ctx.measureText(this.name);
        this.nameHeight = metrics.actualBoundingBoxAscent; //+ metrics.actualBoundingBoxDescent;
        this.nameWidth = metrics.width;
    }

    updatePosition(x: number, y: number): void {
        if (this.isMoving) {
            this.hasNext = true;

            const nextX = this.x + this.deltaX * (this.moveCounterConst - this.moveCounter);
            this.nextDeltaX = (x - nextX) / this.moveCounterConst;

            const nextY = this.y + this.deltaY * (this.moveCounterConst - this.moveCounter);
            this.nextDeltaY = (y - nextY) / this.moveCounterConst;
        }
        this.deltaX = (x - this.x) / this.moveCounterConst;
        this.deltaY = (y - this.y) / this.moveCounterConst;
        this.isMoving = true;
        this.moveCounter = 0;
        this.hasNext = false;
    }

    draw(): void {
        if (this.isHidden) return;

        this.calcDims();
        canv.ctx.save();
        canv.ctx.beginPath();
        canv.ctx.fillStyle = this.color;
        canv.ctx.lineWidth = canv.actionManager.getZoomConstant(5);
        canv.ctx.strokeStyle = "white";
        canv.ctx.lineCap = "round";

        const headlen: number = canv.actionManager.getZoomConstant(12);
        const viewport: Viewport = getViewport();

        let tox: number = this.x;
        let toy: number = this.y;

        let fromx: number = tox + canv.actionManager.getZoomConstant(12);
        let fromy: number = toy + canv.actionManager.getZoomConstant(12);

        let blockPositionX: number = tox + headlen;
        let blockPositionY: number = toy + headlen;
        const blockWidth: number = this.nameWidth + 2 * this.padding;
        const blockHeight: number = this.nameHeight + 2 * this.padding;
        const inlineCursorAdditionalGap: number = canv.actionManager.getZoomConstant(10);
        const topHeight: number = canv.actionManager.getZoomConstant(50);

        if (toy < viewport.viewportStartY + topHeight) {
            tox = this.x;
            if (tox < viewport.viewportStartX + headlen) {
                tox = viewport.viewportStartX + headlen;
            } else if (tox > viewport.viewportStartX + viewport.viewportWidth - headlen) {
                tox = viewport.viewportStartX + viewport.viewportWidth - headlen;
            }
            fromx = tox;
            toy = viewport.viewportStartY + topHeight;
            fromy = toy + headlen;
            blockPositionX = tox - blockWidth / 2;
            if (blockPositionX < viewport.viewportStartX) {
                blockPositionX = viewport.viewportStartX;
            } else if (
                blockPositionX + blockWidth >
                viewport.viewportStartX + viewport.viewportWidth
            ) {
                blockPositionX = viewport.viewportStartX + viewport.viewportWidth - blockWidth;
            }
            blockPositionY = toy + headlen + inlineCursorAdditionalGap;
        } else if (toy > viewport.viewportStartY + viewport.viewportHeight) {
            tox = this.x;
            if (tox < viewport.viewportStartX + headlen) {
                tox = viewport.viewportStartX + headlen;
            } else if (tox > viewport.viewportStartX + viewport.viewportWidth - headlen) {
                tox = viewport.viewportStartX + viewport.viewportWidth - headlen;
            }
            fromx = tox;
            if (tox < viewport.viewportStartX) {
                tox = viewport.viewportStartX;
            } else if (fromx > viewport.viewportStartX + viewport.viewportWidth) {
                blockPositionX = viewport.viewportStartX + viewport.viewportWidth;
            }
            toy = viewport.viewportStartY + viewport.viewportHeight;
            fromy = toy - headlen;
            blockPositionX = tox - blockWidth / 2;
            if (blockPositionX < viewport.viewportStartX) {
                blockPositionX = viewport.viewportStartX;
            } else if (
                blockPositionX + blockWidth >
                viewport.viewportStartX + viewport.viewportWidth
            ) {
                blockPositionX = viewport.viewportStartX + viewport.viewportWidth - blockWidth;
            }
            blockPositionY = toy - headlen - inlineCursorAdditionalGap - blockHeight;
        } else if (tox < viewport.viewportStartX) {
            tox = viewport.viewportStartX;
            fromx = tox + headlen;
            toy = this.y;
            fromy = toy;
            blockPositionX = tox + headlen + inlineCursorAdditionalGap;
            blockPositionY = toy - headlen / 2;
            if (blockPositionY < viewport.viewportStartY + topHeight) {
                blockPositionY = viewport.viewportStartY + topHeight;
            } else if (
                blockPositionY + blockHeight >
                viewport.viewportStartY + viewport.viewportHeight
            ) {
                blockPositionY = viewport.viewportStartY + viewport.viewportHeight - blockHeight;
            }
        } else if (tox > viewport.viewportStartX + viewport.viewportWidth) {
            tox = viewport.viewportStartX + viewport.viewportWidth;
            fromx = tox - headlen;
            toy = this.y;
            fromy = toy;
            blockPositionX = tox - headlen - inlineCursorAdditionalGap - blockWidth;
            blockPositionY = toy - headlen / 2;
            if (blockPositionY < viewport.viewportStartY + topHeight) {
                blockPositionY = viewport.viewportStartY + topHeight;
            } else if (
                blockPositionY + blockHeight >
                viewport.viewportStartY + viewport.viewportHeight
            ) {
                blockPositionY = viewport.viewportStartY + viewport.viewportHeight - blockHeight;
            }
        }

        const dx: number = tox - fromx;
        const dy: number = toy - fromy;
        const angle: number = Math.atan2(dy, dx);

        canv.ctx.moveTo(tox, toy);
        canv.ctx.lineTo(
            tox - headlen * Math.cos(angle - Math.PI / 6),
            toy - headlen * Math.sin(angle - Math.PI / 6),
        );
        canv.ctx.lineTo(
            tox - headlen * Math.cos(angle + Math.PI / 6),
            toy - headlen * Math.sin(angle + Math.PI / 6),
        );
        canv.ctx.lineTo(tox, toy);
        canv.ctx.stroke();
        canv.ctx.fill();

        canv.ctx.beginPath();
        canv.ctx.fillStyle = this.color;
        this.drawRoundedRect(
            blockPositionX,
            blockPositionY,
            this.nameWidth + 2 * this.padding,
            this.nameHeight + 2 * this.padding,
            canv.actionManager.getZoomConstant(7),
        );

        canv.ctx.stroke();
        canv.ctx.fill();
        canv.ctx.restore();

        canv.ctx.save();
        canv.ctx.beginPath();
        canv.ctx.fillStyle = this.fontColor;
        canv.ctx.font = `${this.fontSize}px sans-serif`;
        canv.ctx.fillText(
            this.name,
            blockPositionX + this.padding,
            blockPositionY + this.nameHeight + this.padding,
        );
        canv.ctx.fill();
        canv.ctx.restore();

        if (this.isMoving) {
            this.moveCounter++;

            if (this.moveCounter === this.moveCounterConst) {
                if (this.hasNext) {
                    this.hasNext = false;
                    this.deltaX = this.nextDeltaX;
                    this.deltaY = this.nextDeltaY;
                    this.isMoving = true;
                    canv.redraw();
                } else {
                    this.isMoving = false;
                }
                this.moveCounter = 0;
            } else {
                this.x += this.deltaX;
                this.y += this.deltaY;
                canv.redraw();
            }
        }
    }

    show() {
        if (this.isHidden) {
            this.isHidden = false;
            canv.redraw();
        }
    }

    hide() {
        if (!this.isHidden) {
            this.isHidden = true;
            canv.redraw();
        }
    }
}
